/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { MediaRecorder as ExtendableMediaRecorder, IMediaRecorder } from 'extendable-media-recorder';

export type StatusMessages =
  | 'media_aborted'
  | 'permission_denied'
  | 'no_specified_media_found'
  | 'media_in_use'
  | 'invalid_media_constraints'
  | 'no_constraints'
  | 'recorder_error'
  | 'idle'
  | 'acquiring_media'
  | 'delayed_start'
  | 'begin'
  | 'recording'
  | 'stopping'
  | 'stopped'
  | 'paused';

export enum RecorderErrors {
  AbortError = 'media_aborted',
  NotAllowedError = 'permission_denied',
  NotFoundError = 'no_specified_media_found',
  NotReadableError = 'media_in_use',
  OverconstrainedError = 'invalid_media_constraints',
  TypeError = 'no_constraints',
  NONE = '',
  NO_RECORDER = 'recorder_error',
}

function getSupportedMimeTypes() {
  const possibleTypes = [
    // 'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=h264,opus',
    'video/mp4;codecs=h264,aac',
  ];
  return possibleTypes.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}

export type ReactMediaRecorderHookProps = {
  audio?: boolean | MediaTrackConstraints;
  video?: boolean | MediaTrackConstraints;
  screen?: boolean;
  onStop?: (blobUrl: string, blob: Blob) => void;
  onStart?: () => void;
  blobPropertyBag?: BlobPropertyBag;
  mediaRecorderOptions?: MediaRecorderOptions | undefined;
  customMediaStream?: MediaStream | null;
  stopStreamsOnStop?: boolean;
  askPermissionOnMount?: boolean;
  onStartConfirmation?: (resolve: any, reject: any) => void;
};

const useScreenRecorder = ({
  onStop = () => null,
  onStart = () => null,
  onStartConfirmation = () => null,
  mediaRecorderOptions = { mimeType: getSupportedMimeTypes()[0], videoBitsPerSecond: 8000000 },
  stopStreamsOnStop = true,
  askPermissionOnMount = false,
}: ReactMediaRecorderHookProps) => {
  const mediaRecorder = useRef<IMediaRecorder | null>(null);
  const mediaChunks = useRef<Blob[]>([]);
  const mediaStream = useRef<MediaStream | null>(null);
  const userStream = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<StatusMessages>('idle');
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(false);
  const [isCameraMuted, setIsCameraMuted] = useState<boolean>(false);
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<keyof typeof RecorderErrors>('NONE');
  const [counter, setCounter] = useState<number>(0); // seconds
  const [beginCounter, setBeginCounter] = useState<number>(4); // seconds to start the countdown

  useEffect(() => {
    let timer: any;
    if (status === 'recording') {
      timer = setInterval(() => setCounter(counter + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [counter, status]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current) {
      if (mediaRecorder.current.state !== 'inactive') {
        setStatus('stopping');
        mediaRecorder.current.stop();
        if (stopStreamsOnStop) {
          if (mediaStream.current) {
            mediaStream.current.getTracks().forEach((track) => track.stop());
          }
        }
        mediaChunks.current = [];
      }
    }
  }, [stopStreamsOnStop]);

  const getMediaStream = useCallback(async () => {
    try {
      const displayMedia = (await navigator.mediaDevices.getDisplayMedia({
        video: true,
      })) as MediaStream;
      displayMedia.getVideoTracks()[0].addEventListener('ended', () => {
        stopRecording();
      });

      const userMedia = (await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
        video: true,
      })) as MediaStream;
      userStream.current = userMedia;

      // @ts-ignore
      const tracks = [...displayMedia.getTracks(), ...userMedia.getTracks()];
      const stream: MediaStream = new MediaStream(tracks);
      mediaStream.current = stream;

      setStatus('idle');
    } catch (e: any) {
      setError(e.name);
      setStatus('idle');
    }
  }, [stopRecording]);

  const onRecordingActive = ({ data }: BlobEvent) => {
    mediaChunks.current.push(data);
  };

  const onRecordingStart = useCallback(async () => {
    setMediaBlob(null);
    setMediaBlobUrl(undefined);
    setCounter(0);
    setBeginCounter(4);
    onStart();
  }, [onStart]);

  const onRecordingStop = useCallback(async () => {
    const blob = new Blob(mediaChunks.current, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    setStatus('stopped');
    setCounter(0);
    setBeginCounter(4);
    setMediaBlob(blob);
    setMediaBlobUrl(url);
    onStop(url, blob);
  }, [onStop]);

  const startRecording = async () => {
    setError('NONE');
    if (!mediaStream.current) {
      await getMediaStream();
    }
    if (mediaStream.current) {
      const isStreamEnded = mediaStream.current
        .getTracks()
        .some((track) => track.readyState === 'ended');
      if (isStreamEnded) {
        await getMediaStream();
      }

      // User blocked the permissions (getMediaStream errored out)
      if (!mediaStream.current.active) {
        return;
      }

      // waiting for user confimation before recording
      if (onStartConfirmation) {
        await new Promise((resolve, reject) => {
          onStartConfirmation(resolve, reject);
        });
      }

      setStatus('begin');
    }
  };

  useEffect(() => {
    let timer: any;
    if (status === 'begin') {
      if (beginCounter > 0) {
        timer = setInterval(() => setBeginCounter(beginCounter - 1), 1000);
      } else {
        if (!mediaStream || !mediaStream.current || !mediaStream.current.active) {
          return;
        }
        mediaRecorder.current = new ExtendableMediaRecorder(
          mediaStream.current,
          mediaRecorderOptions || undefined,
        );
        mediaRecorder.current.ondataavailable = onRecordingActive;
        mediaRecorder.current.onstop = onRecordingStop;
        mediaRecorder.current.onstart = onRecordingStart;
        mediaRecorder.current.onerror = () => {
          setError('NO_RECORDER');
          setStatus('idle');
        };
        mediaRecorder.current.start();
        setStatus('recording');
      }
    }
    return () => clearInterval(timer);
  }, [beginCounter, mediaRecorderOptions, onRecordingStart, onRecordingStop, status]);

  const muteAudio = (mute: boolean) => {
    setIsAudioMuted(mute);
    if (mediaStream.current) {
      mediaStream.current
        .getAudioTracks()
        // eslint-disable-next-line no-return-assign, no-param-reassign
        .forEach((track) => track.enabled = !mute);
    }
  };

  const muteCamera = (mute: boolean) => {
    setIsCameraMuted(mute);
    if (userStream.current) {
      userStream.current
        .getVideoTracks()
        // eslint-disable-next-line no-return-assign, no-param-reassign
        .forEach((track) => track.enabled = !mute);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setStatus('paused');
      mediaRecorder.current.pause();
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      setStatus('recording');
      mediaRecorder.current.resume();
    }
  };

  useEffect(() => {
    if (!window.MediaRecorder) {
      throw new Error('Unsupported Browser');
    }
    if (!mediaStream.current && askPermissionOnMount) {
      getMediaStream();
    }
    return () => {
      if (mediaStream.current) {
        const tracks = mediaStream.current.getTracks();
        tracks.forEach((track) => track.clone().stop());
      }
    };
  }, [askPermissionOnMount, getMediaStream]);

  return {
    error: RecorderErrors[error],
    muteAudio: () => muteAudio(true),
    unMuteAudio: () => muteAudio(false),
    muteCamera: () => muteCamera(true),
    unMuteCamera: () => muteCamera(false),
    mediaBlob,
    mediaBlobUrl,
    pauseRecording,
    resumeRecording,
    startRecording,
    stopRecording,
    status,
    isAudioMuted,
    isCameraMuted,
    counter,
    beginCounter,
    previewScreenStream: mediaStream.current
      ? new MediaStream(mediaStream.current.getVideoTracks())
      : null,
    previewWebcamStream: userStream.current
      ? new MediaStream(userStream.current.getVideoTracks())
      : null,
    clearBlobUrl: () => {
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
      setMediaBlob(null);
      setMediaBlobUrl(undefined);
      setCounter(0);
      setBeginCounter(4);
      setStatus('idle');
    },
  };
};

export default useScreenRecorder;
